<div class="package-container">
  @if (deepDive) {
    <div class="deep-dive">
      <iframe
        [src]="deepDive"
        width="560"
        height="315"
        frameborder="0"
        webkitallowfullscreen
        mozallowfullscreen
        allowfullscreen
      ></iframe>
    </div>
  }

  <div class="package-description">
    <hyperkit-badge
      variant="neutral"
      text="Description"
      id="description"
    ></hyperkit-badge>
    @if (data?.description) {
      <hyperkit-markdown
        color="secondary"
        [md]="data?.description"
      ></hyperkit-markdown>
    } @else {
      <hyperkit-typography variant="body" size="md" color="secondary"
        >--</hyperkit-typography
      >
    }
  </div>

  <div class="package-info">
    @if (data?.created_date) {
      <div id="created-date" class="info-item">
        <hyperkit-badge variant="neutral" text="Created"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.created_date | date
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.last_updated) {
      <div id="last-updated" class="info-item">
        <hyperkit-badge variant="neutral" text="Last Updated"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.last_updated | date
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.severity) {
      <div id="severity" class="info-item">
        <hyperkit-badge variant="neutral" text="Severity"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.severity
        }}</hyperkit-typography>
      </div>
    }

    @if (threatProfiles$ | async; as threatProfile) {
      <div id="threat-names" class="info-item">
        <hyperkit-badge variant="neutral" text="Threat Names"></hyperkit-badge>
        @if (threatProfile.length > 0) {
          @for (threat of threatProfile; track threat; let last = $last) {
            <hyperkit-link
              routerLink="/research/threat-profile/{{ threat.profile_id }}"
              variant="body"
              size="md"
              color="secondary"
              display="inline"
              [text]="threat.profile_name + (last ? '' : ', ')"
            ></hyperkit-link>
          }
        } @else {
          <hyperkit-typography variant="body" size="md" color="secondary">{{
            data?.context?.threat_names?.join(', ')
          }}</hyperkit-typography>
        }
      </div>
    }

    @if (data?.context?.threat_categories?.length > 0) {
      <div id="threat-categories" class="info-item">
        <hyperkit-badge
          variant="neutral"
          text="Threat Categories"
        ></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.threat_categories.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.threat_types?.length > 0) {
      <div id="threat-types" class="info-item">
        <hyperkit-badge variant="neutral" text="Threat Types"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.threat_types.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.source_regions?.length > 0) {
      <div id="source-regions" class="info-item">
        <hyperkit-badge variant="neutral" text="Source Regions"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.source_regions.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.source_countries?.length > 0) {
      <div id="source-countries" class="info-item">
        <hyperkit-badge
          variant="neutral"
          text="Source Countries"
        ></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.source_countries.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.actors?.length > 0) {
      <div id="actors" class="info-item">
        <hyperkit-badge variant="neutral" text="Actors"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.actors.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.target_regions?.length > 0) {
      <div id="target-regions" class="info-item">
        <hyperkit-badge variant="neutral" text="Target Regions"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.target_regions.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.target_countries?.length > 0) {
      <div id="target-countries" class="info-item">
        <hyperkit-badge
          variant="neutral"
          text="Target Countries"
        ></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.target_countries.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.target_oses?.length > 0) {
      <div id="target-oses" class="info-item">
        <hyperkit-badge variant="neutral" text="Target OSes"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.target_oses.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.target_os_versions?.length > 0) {
      <div id="target-os-versions" class="info-item">
        <hyperkit-badge
          variant="neutral"
          text="Target OS Versions"
        ></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.target_os_versions.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.dependencies?.length > 0) {
      <div id="dependencies" class="info-item">
        <hyperkit-badge variant="neutral" text="Dependencies"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.dependencies.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.mapping?.diamond_model) {
      <div id="diamond-model" class="info-item">
        <hyperkit-badge variant="neutral" text="Diamond Model"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.mapping.diamond_model
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.mapping?.kill_chains?.length > 0) {
      <div id="kill-chains" class="info-item">
        <hyperkit-badge variant="neutral" text="Kill Chains"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.mapping.kill_chains.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.campaigns?.length > 0) {
      <div id="campaigns" class="info-item">
        <hyperkit-badge variant="neutral" text="Campaigns"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.campaigns.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.mapping?.exploit_or_vulns?.length > 0) {
      <div id="exploits-vulns" class="info-item">
        <hyperkit-badge variant="neutral" text="Exploits/Vulns"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.mapping.exploit_or_vulns.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.context?.operations?.length > 0) {
      <div id="operations" class="info-item">
        <hyperkit-badge variant="neutral" text="Operations"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.context.operations.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.mapping?.mitre?.mitre_attack_tactics?.length > 0) {
      <div id="mitre-attack-tactics" class="info-item">
        <hyperkit-badge
          variant="neutral"
          text="MITRE Attack Tactics"
        ></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.mapping.mitre.mitre_attack_tactics.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.mapping?.mitre?.mitre_attack_techniques?.length > 0) {
      <div id="mitre-attack-techniques" class="info-item">
        <hyperkit-badge
          variant="neutral"
          text="MITRE Attack Techniques"
        ></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.mapping.mitre.mitre_attack_techniques.join(', ')
        }}</hyperkit-typography>
      </div>
    }

    @if (data?.mapping?.cwe_ids?.length > 0) {
      <div id="cwe-ids" class="info-item">
        <hyperkit-badge variant="neutral" text="CWE IDs"></hyperkit-badge>
        <hyperkit-typography variant="body" size="md" color="secondary">{{
          data?.mapping.cwe_ids.join(', ')
        }}</hyperkit-typography>
      </div>
    }
  </div>

  @if (relatedCollections$ | async; as collections) {
    @if (collections.length > 0) {
      <div id="related-collections" class="package-footer">
        <hyperkit-badge
          variant="neutral"
          text="Related Collections"
        ></hyperkit-badge>
        <div>
          @for (collection of collections; track collection; let last = $last) {
            <hyperkit-link
              (click)="navigateToCollection(collection.UUID)"
              variant="body"
              size="md"
              color="secondary"
              display="inline"
              [text]="collection.title + (last ? '' : ', ')"
            ></hyperkit-link>
          }
        </div>
      </div>
    }
  }

  @if (tags?.length > 0) {
    <div class="package-tags">
      <cyborg-facet-buttons
        [closable]="false"
        class="tile-facets"
        [facets]="tags"
        [groupByCategory]="false"
      ></cyborg-facet-buttons>
    </div>
  }
</div>
